import { privateRequest } from "../axios";
import type { ApiResponse, ExportFileResult, ListApiResponse } from "@/types/common";
import { parseFilenameFromContentDisposition } from "@/lib/downloadExcel";

// Confirmed real endpoints (GET/POST /api/admin/chapters, GET
// /api/admin/chapters/{id}), given directly by the user.
export interface Chapter {
  id: number;
  name: string;
  venues: string[];
  membersCount: number;
  eventsCount: number;
  waitlistCount: number;
  // A display name on read, not a user id — POST's `leadUser` is the id
  // that resolves to this string.
  leadUser: string | null;
}

export interface CreateChapterPayload {
  chapterName: string;
  leadUser: number;
}

// Per the user: editing reuses the exact same `POST /admin/chapters`
// endpoint and method as `createChapter` below — no separate update
// route — with the same body shape plus a real `chapterId` (mirroring
// decision #45's captured create body, which included `chapterId`
// alongside `chapterName`/`leadUser`). `leadUser` stays optional here
// (unlike create) since "Manage chapter" may only change the city name
// without touching the lead.
export interface UpdateChapterPayload {
  chapterId: number;
  chapterName: string;
  leadUser?: number;
}

export interface GetChaptersParams {
  pageNo?: number;
  perPage?: number;
  // Not independently confirmed on this endpoint — extended on the same
  // `isExport: true`-returns-a-file convention confirmed for `GET
  // /admin/action-logs` (see docs/decisions.md #74), per the user's
  // explicit instruction to apply it to every export in the project.
  // Only `exportChapters` below sets this.
  isExport?: boolean | undefined;
}

const ENDPOINTS = {
  CHAPTERS: "/chapters",
};

const getChapters = async (params?: GetChaptersParams) => {
  const response = await privateRequest.get<ListApiResponse<Chapter>>(ENDPOINTS.CHAPTERS, { params });
  return response.data;
};

// Same `isExport: true`-returns-a-file convention as
// `services/api/auditLog.ts`'s `exportActionLogs` — see docs/decisions.md
// #74.
const exportChapters = async (params?: Omit<GetChaptersParams, "isExport">): Promise<ExportFileResult> => {
  const response = await privateRequest.get(ENDPOINTS.CHAPTERS, {
    params: { ...params, isExport: true },
    responseType: "blob",
  });
  return {
    blob: response.data as Blob,
    fileName: parseFilenameFromContentDisposition(response.headers["content-disposition"]),
  };
};

const getChapterById = async (chapterId: number) => {
  const response = await privateRequest.get<ApiResponse<Chapter>>(`${ENDPOINTS.CHAPTERS}/${chapterId}`);
  return response.data;
};

const createChapter = async (payload: CreateChapterPayload) => {
  const response = await privateRequest.post(ENDPOINTS.CHAPTERS, payload);
  return response.data;
};

const updateChapter = async (payload: UpdateChapterPayload) => {
  const response = await privateRequest.post(ENDPOINTS.CHAPTERS, payload);
  return response.data;
};

const chapterService = {
  getChapters,
  exportChapters,
  getChapterById,
  createChapter,
  updateChapter,
};

export default chapterService;
